import { useEffect, useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { Cell, Pill, Row, SectionHeader, Table, when } from './kit';

interface Overview {
  limit: number | null;
  inUse: number;
  holders: { id: string; name: string; role: string | null; email: string | null; since: string; pageOpen: boolean; heldUntil: string }[];
  days: { day: string; peak: number; refused: number; licenses: number | null }[];
}

const ROLE: Record<string, string> = { agent: 'Agent', supervisor: 'Supervisor', qa: 'QA', manager: 'Manager' };
const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Concurrent licenses: how many people may be signed in at once (admins
 * excepted), who holds a seat now, and the daily peaks. Seats free up on
 * sign-out, or a couple of minutes after someone closes Nova.
 */
export function Licenses() {
  const [data, setData] = useState<Overview | null>(null);
  const load = () => {
    api<Overview>('GET', '/licenses')
      .then(setData)
      .catch(() => setData(null));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, []);

  async function signOut(id: string, name: string) {
    const ok = await act(() => api('POST', `/agents/${id}/sign-out`).then(() => true), `${name} signed out; their license is free`);
    if (ok) load();
  }

  const full = data?.limit !== null && data !== null && data.inUse >= (data.limit ?? Infinity);
  return (
    <>
      <SectionHeader
        title="Licenses"
        lead="Licenses limit how many people can be signed in at once, however many users you have. One person uses one license on any number of tabs or devices. Admins never use one."
      />
      {!data ? (
        <p className="text-sm text-ink-3">Loading…</p>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-baseline gap-x-6 gap-y-2" aria-label="Licenses in use">
            <p className="tabular text-3xl font-semibold">
              {data.inUse}
              {data.limit !== null ? <span className="text-ink-3"> of {data.limit}</span> : null}
            </p>
            <p className="text-sm text-ink-2">
              {data.limit === null
                ? 'signed in now. Your plan has no license limit.'
                : full
                  ? 'in use: all licenses are taken, so new sign-ins are refused until one is free.'
                  : `in use; ${data.limit - data.inUse} free.`}
            </p>
          </div>

          <h3 className="mb-2 text-sm font-medium">Using a license now</h3>
          <Table head={['Person', 'Role', 'Since', 'Status', '']} empty={data.holders.length === 0 && 'Nobody is signed in.'}>
            {data.holders.map((h) => (
              <Row key={h.id}>
                <Cell>
                  <span className="block">{h.name}</span>
                  <span className="text-xs text-ink-3">{h.email}</span>
                </Cell>
                <Cell className="text-ink-2">{h.role ? (ROLE[h.role] ?? h.role) : ''}</Cell>
                <Cell className="whitespace-nowrap text-ink-2">{when(h.since)}</Cell>
                <Cell>{h.pageOpen ? <Pill tone="good">Nova open</Pill> : <Pill tone="warn">Closed, frees at {time(h.heldUntil)}</Pill>}</Cell>
                <Cell className="text-right">
                  <Button tone="ghost" className="px-2.5 py-1 text-xs" onClick={() => void signOut(h.id, h.name)} title="Ends their session and frees the license">
                    Sign out
                  </Button>
                </Cell>
              </Row>
            ))}
          </Table>

          <h3 className="mb-2 mt-8 text-sm font-medium">The last 30 days</h3>
          <Table head={['Day', 'Most at once', 'Sign-ins refused']} empty={data.days.length === 0 && 'No use recorded yet.'}>
            {data.days.map((d) => (
              <Row key={d.day}>
                <Cell className="tabular">{d.day}</Cell>
                <Cell className="tabular">
                  {d.peak}
                  {/* Against that day's license count, which may differ from today's */}
                  {d.licenses !== null ? <span className="text-ink-3"> of {d.licenses}</span> : null}
                </Cell>
                <Cell className={`tabular ${d.refused ? 'text-break' : 'text-ink-3'}`}>{d.refused}</Cell>
              </Row>
            ))}
          </Table>
          <p className="mt-2 text-xs text-ink-3">Refused sign-ins mean people were turned away because every license was in use: a sign you may need more.</p>
        </>
      )}
    </>
  );
}
